import {pollAsync} from '@yourcoach/async-utils';
import {i18n} from '@yourcoach/i18n';

import type {Nullable} from '../../utils/utility-types';

import UpdateAppNativeSvg from '../../assets/icons-colored/update-app.native.svg';
import UpdateAppWebSvg from '../../assets/icons-colored/update-app.web.svg';
import {alert} from '../../components/alert';
import {envManager} from '../../utils/env';
import {logger} from '../../utils/logger';
import {platform} from '../../utils/platform';
import {reloadApp} from '../../utils/reloadApp';
import {compareVersions} from '../../utils/semver';
import {storage} from '../../utils/storage';
import {lookupVersion} from './lookup-version';

// use it for testing
const DEV_REMOTE_VERSION = '0.0.0';

const isWeb = platform.isWeb;

export class AppNewVersionService {
  private checkTimeout: number;
  private laterCheckInterval = 60 * 60 * 1000;
  private launchCheckInterval = 10 * 1000;
  private regularCheckInterval = 5 * 60 * 1000;
  private storageKey = 'anv';
  private unsubscribeFromStorage: Nullable<() => void> = null;

  constructor(
    private currentVersion: string,
    private options: AppNewVersionService.Props,
  ) {
    storage.setItem(this.storageKey, this.currentVersion);
    this.scheduleCheck(this.launchCheckInterval);
    this.listenForStorageChanges();
  }

  destruct() {
    clearTimeout(this.checkTimeout);

    this.unsubscribeFromStorage?.();
  }

  private get message() {
    if (this.options.message) {
      return this.options.message;
    }

    if (platform.isNative) {
      return i18n.t('shared:update_app_for_latest_features');
    }

    return i18n.t('shared:reload_page_for_latest_features');
  }

  private async checkForNewVersion() {
    try {
      await pollAsync(() => envManager.isReady);

      const newVersion = await this.lookupNewVersion();

      if (newVersion && compareVersions(newVersion, this.currentVersion) > 0) {
        this.showNewVersionNotification(newVersion);
      } else {
        this.scheduleCheck(this.regularCheckInterval);
      }
    } catch (error) {
      logger.error(error);
    }
  }

  private listenForStorageChanges() {
    if (!isWeb) return;

    this.unsubscribeFromStorage = storage.onChange(
      this.storageKey,
      newValue => {
        if (newValue !== this.currentVersion) {
          // reload the app if we detected a new version
          reloadApp();
        }
      },
    );
  }

  private async lookupNewVersion() {
    if (envManager.get('appEnv') === 'dev') {
      return DEV_REMOTE_VERSION;
    }

    return await lookupVersion(this.options);
  }

  private onUpdateClick(newVersion: string) {
    logger.event('new_version_popup_update_tap');

    // to trigger reload on other tabs
    storage.setItem(this.storageKey, newVersion);

    if (this.options.onUpdateClick) {
      this.options.onUpdateClick();
    } else {
      reloadApp();
    }
  }

  private scheduleCheck(interval: number) {
    this.checkTimeout = +setTimeout(() => {
      this.checkForNewVersion();
    }, interval);
  }

  private showNewVersionNotification(newVersion: string) {
    logger.event('new_version_popup_view');

    alert.show({
      buttons: [
        {
          label: i18n.t('shared:update'),
          onClick: () => {
            this.onUpdateClick(newVersion);
          },
          scheme: 'primary',
        },
      ],
      icon: platform.isWeb ? UpdateAppWebSvg : UpdateAppNativeSvg,
      iconSize: {
        height: 100,
        width: 160,
      },
      isDismissible: true,
      message: this.message,
      onDismiss: () => {
        logger.event('new_version_popup_close_tap');

        this.scheduleCheck(this.laterCheckInterval);
      },
      title: i18n.t('shared:new_app_version_available'),
    });
  }
}

export namespace AppNewVersionService {
  export type Props = VersionLookupProps & {
    message?: string;
    onUpdateClick?: () => void;
  };

  export type VersionLookupProps =
    | VersionLookupPropsNative
    | VersionLookupPropsWeb;

  export type VersionLookupPropsNative = {
    /**
     * It's the name of the app in the package.json
     * It needed for finding the version in the S3 bucket
     */
    appName: string;
    bundleId: string;
  };

  export type VersionLookupPropsWeb = {
    versionUrl: string;
  };
}
